import React, { useRef } from 'react';
import ReactECharts from 'echarts-for-react';

// Variante apilada del gráfico "% de recursos transferidos por Tipo de subsidio por Año".
// Una sola barra por año que representa el 100%; cada segmento apilado muestra el % que
// ocupa esa categoría (Municipio vs ONG/Universidades) de ese 100%.
export const GraficoRecursosPorTipoAnualApilado = ({ data = [], anioSeleccionado = 'Todos', soloChart = false, onChartReady = null }) => {

    // 📥 Referencia a la instancia de ECharts para descargar el gráfico desde el botón del header
    const chartRef = useRef(null);

    const formatPesos = (valor) => {
        if (valor >= 1000000000) return `$${(valor / 1000000000).toFixed(2)}B`;
        if (valor >= 1000000) return `$${(valor / 1000000).toFixed(2)}M`;
        return `$${Math.round(valor).toLocaleString('es-AR')}`;
    };

    // Formatear % con coma decimal y 1 decimal
    const formatPorc = (valor) => `${valor.toFixed(1).replace('.', ',')}%`;

    // Determina si un año debe verse "pleno" según el filtro seleccionado.
    // Soporta anioSeleccionado como string ('2025', 'Todos') o array (multi-año).
    const estaActivo = (anio) => {
        const sel = anioSeleccionado;
        if (sel === undefined || sel === null) return true;
        if (Array.isArray(sel)) {
            if (sel.length === 0) return true;
            return sel.map(String).includes(String(anio));
        }
        const s = String(sel);
        if (s === 'Todos' || s === 'Todas') return true;
        return s === String(anio);
    };

    const descargarGrafico = () => {
        const instancia = chartRef.current?.getEchartsInstance?.();
        if (!instancia) return;
        const url = instancia.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
        const link = document.createElement('a');
        link.href = url;
        link.download = `Recursos_Transferidos_Apilado_${anioSeleccionado}.png`;
        link.click();
    };

    // Rango completo de años a mostrar SIEMPRE en el eje X (años sin datos van en 0)
    const ANIO_MIN = 2016;
    const ANIO_MAX = 2026;

    // Indexamos los montos por año (los que vienen del backend)
    const porAnio = new Map(data.map(item => [Number(item.anio), item]));
    const anos = [];
    for (let a = ANIO_MIN; a <= ANIO_MAX; a++) anos.push(a);

    // Para cada año calculamos la suma de las partes (pct Apilado = pct por separado) y
    // guardamos también el monto ($) de cada categoría para el tooltip.
    // Como cada segmento es su propio % sobre el total anual, la suma da 100%.
    const dataMunicipio = anos.map(anio => {
        const item = porAnio.get(anio) || {};
        const total = (item.municipio || 0) + (item.ong || 0);
        return {
            pct: total > 0 ? ((item.municipio || 0) / total) * 100 : 0,
            monto: item.municipio || 0
        };
    });
    const dataOng = anos.map(anio => {
        const item = porAnio.get(anio) || {};
        const total = (item.municipio || 0) + (item.ong || 0);
        return {
            pct: total > 0 ? ((item.ong || 0) / total) * 100 : 0,
            monto: item.ong || 0
        };
    });

    // 🎨 Definimos los colores base y sus variantes con opacidad para el "desenfocado"
    const colorMunicipioBase = '#0284c7';
    const colorMunicipioTenue = 'rgba(2, 132, 199, 0.25)';

    const colorOngBase = '#10b981';
    const colorOngTenue = 'rgba(16, 185, 129, 0.25)';

    // 🎛️ Configuración de opciones para ECharts (barras apiladas al 100%)
    const option = {
        color: [colorMunicipioBase, colorOngBase],

        tooltip: {
            trigger: 'axis',
            backgroundColor: '#1e293b',
            borderWidth: 0,
            textStyle: { color: '#ffffff', fontSize: 12 },
            axisPointer: { type: 'shadow' },
            padding: [10, 15],
            formatter: function (params) {
                let res = `<div style="font-weight: bold; margin-bottom: 4px; color: #94a3b8;">Año ${params[0].name}</div>`;
                let total = 0;
                params.forEach(item => {
                    const d = item.data || {};
                    const monto = Number(d.monto) || 0;
                    const pct = Number(d.pct) || 0;
                    total += monto;
                    res += `<div style="display: flex; justify-content: space-between; gap: 15px; align-items: center;">
                                <span>${item.marker} ${item.seriesName}:</span>
                                <span style="font-weight: bold;">${formatPesos(monto)} (${formatPorc(pct)})</span>
                            </div>`;
                });
                res += `<div style="display: flex; justify-content: space-between; gap: 15px; align-items: center; border-top: 1px solid #334155; margin-top: 4px; padding-top: 4px;">
                            <span>Total:</span>
                            <span style="font-weight: bold;">${formatPesos(total)}</span>
                        </div>`;
                return res;
            }
        },
        legend: {
            data: ['Subsidio a Municipio', 'Subsidio a ONG/Universidades'],
            right: '0%',
            top: '5%',
            icon: 'circle',
            itemWidth: 8,
            textStyle: { color: '#64748b', fontSize: 12 }
        },
        grid: {
            left: '3%',
            right: '5%',
            bottom: '3%',
            top: '15%',
            containLabel: true
        },
        xAxis: [
            {
                type: 'category',
                data: anos,
                axisTick: { show: false },
                axisLine: { lineStyle: { color: '#cbd5e1' } },
                axisLabel: {
                    formatter: function (value) {
                        return estaActivo(value) ? `{selected|${value}}` : value;
                    },
                    rich: {
                        selected: {
                            fontWeight: 'bold',
                            color: '#1e293b'
                        }
                    },
                    color: '#94a3b8',
                    fontSize: 11,
                    margin: 12
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                min: 0,
                max: 100,
                splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
                axisLine: { show: false },
                axisLabel: { color: '#94a3b8', fontSize: 11, formatter: '{value}%' }
            }
        ],
        series: [
            {
                name: 'Subsidio a Municipio',
                type: 'bar',
                stack: 'total',
                barWidth: 42,
                data: dataMunicipio.map((d, i) => ({
                    value: Number(d.pct.toFixed(2)),
                    monto: d.monto,
                    pct: Number(d.pct.toFixed(2)),
                    anio: anos[i]
                })),
                label: {
                    show: true,
                    position: 'inside',
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 'bold',
                    formatter: function (p) {
                        const pct = p.data && Number(p.data.pct) || 0;
                        return pct > 0 ? formatPorc(pct) : "";
                    }
                },
                itemStyle: {
                    borderWidth: 1,
                    borderColor: '#ffffff',
                    color: function (params) {
                        const anioFila = anos[params.dataIndex]?.toString();
                        if (estaActivo(anioFila)) {
                            return colorMunicipioBase;
                        }
                        return colorMunicipioTenue;
                    }
                }
            },
            {
                name: 'Subsidio a ONG/Universidades',
                type: 'bar',
                stack: 'total',
                barWidth: 42,
                data: dataOng.map((d, i) => ({
                    value: Number(d.pct.toFixed(2)),
                    monto: d.monto,
                    pct: Number(d.pct.toFixed(2)),
                    anio: anos[i]
                })),
                label: {
                    show: true,
                    position: 'inside',
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 'bold',
                    formatter: function (p) {
                        const pct = p.data && Number(p.data.pct) || 0;
                        return pct > 0 ? formatPorc(pct) : "";
                    }
                },
                itemStyle: {
                    borderWidth: 1,
                    borderColor: '#ffffff',
                    color: function (params) {
                        const anioFila = anos[params.dataIndex]?.toString();
                        if (estaActivo(anioFila)) {
                            return colorOngBase;
                        }
                        return colorOngTenue;
                    }
                }
            }
        ]
    };

    if (soloChart) {
        return (
            <div className="flex-1 w-full min-h-0">
                <ReactECharts
                    ref={chartRef}
                    option={option}
                    onChartReady={(instancia) => onChartReady?.(instancia)}
                    style={{ height: '100%', width: '100%' }}
                    notMerge={true}
                />
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm h-[450px] flex flex-col">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-base font-bold text-gray-800 tracking-tight uppercase">
                        % de recursos transferidos por Tipo de subsidio por Año (Apilado)
                    </h3>
                    <p className="text-xs text-gray-400">
                        Barra = 100% anual · cada segmento es el % que ocupa (Municipios y ONGs/Universidades)
                    </p>
                </div>
                <button
                    type="button"
                    onClick={descargarGrafico}
                    title="Descargar gráfico como PNG"
                    className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200 transition-colors select-none"
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    <span className="font-medium text-gray-700">Descargar</span>
                </button>
            </div>

            <div className="flex-1 w-full min-h-0">
                <ReactECharts
                    ref={chartRef}
                    option={option}
                    style={{ height: '100%', width: '100%' }}
                    notMerge={true}
                />
            </div>
        </div>
    );
};
